import { Eye, Plus, Check } from 'lucide-react'
import { Card } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { TeamLogo } from './team-logo'
import type { Team } from '../../types'

export interface SpotButtonState {
  text: string
  variant: 'default' | 'secondary' | 'outline'
  disabled: boolean
  loading: boolean
  isSpotted: boolean
}

interface CompactTeamCardProps {
  team: Team
  isSpotted: boolean
  onSpot: (teamId: string) => void
  onViewDetails: (team: Team) => void
  spotButtonState: SpotButtonState
  className?: string
}

export function CompactTeamCard({
  team,
  isSpotted,
  onSpot,
  onViewDetails,
  spotButtonState,
  className
}: CompactTeamCardProps) {
  return (
    <Card
      className={`group relative overflow-hidden hover:shadow-md transition-all cursor-pointer border border-gray-200 ${className || ''}`}
      onClick={() => onViewDetails(team)}
    >
      {/* Spotted indicator */}
      {isSpotted && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-green-500 text-white rounded-full p-1">
            <Check className="h-3 w-3" />
          </div>
        </div>
      )}

      <div className="p-3">
        {/* Logo and main info */}
        <div className="flex items-start space-x-3 mb-3">
          <div className="flex-shrink-0">
            <TeamLogo
              team={team}
              size={40}
              className="rounded-md border border-gray-100"
            />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm leading-tight mb-1 line-clamp-2">
              {team.name}
            </h3>
            <p className="text-xs text-gray-600 truncate">
              {team.city}{team.state && `, ${team.state}`}
            </p>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge variant="outline" className="text-xs px-2 py-0.5 font-medium">
            {team.league}
          </Badge>
          <Badge variant="secondary" className="text-xs px-2 py-0.5">
            {team.sport}
          </Badge>
        </div>

        {/* Actions */}
        <div className="flex gap-1.5">
          <Button
            onClick={(e) => {
              e.stopPropagation()
              onViewDetails(team)
            }}
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs"
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>

          {!isSpotted && (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                onSpot(team.id)
              }}
              disabled={spotButtonState.disabled}
              size="sm"
              className="flex-1 h-8 text-xs"
              variant={spotButtonState.variant}
            >
              {spotButtonState.isSpotted ? (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Spotted
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1" />
                  Spot
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
